import { RingQueue, Websocket, WebsocketBuilder } from "websocket-ts";
import { getEnumKeyByEnumValue } from "@/shared/utils/enum";
import { Channel } from "@/websocket/model/channel";
import { ChannelType } from "@/websocket/model/enums/channel-type";
import { MessageType } from "@/websocket/model/enums/message-type";
import {
    ConnectionRequest,
    PingRequest,
    Request,
    SubscribeRequest,
    UnsubscribeRequest,
} from "@/websocket/model/messages/requests";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import { useWsStore } from "@/websocket/stores/use-ws-store";
import { ref, Ref, toValue } from "vue";

// Turn off for production server and keep only for development on localhost
// const extendedDebugging = isDevelopmentEnv();
const extendedDebugging = false; // cluttering console with too much info

function trimEndSlash(s: string) {
    return s?.replace(/\/*$/, "");
}
const baseUrl = trimEndSlash(environment.settings["Integrations:WS:BaseAddress"]);
const getWsUrl = () => `${baseUrl}/websock`;

// Central place where we set OB protocol version (not likely we'll revert to version 1 but just in case)
const PROTOCOL_VERSION = 2;
export const isV2Protocol = () => PROTOCOL_VERSION === 2;

export default function useOpenbetWsClient() {
    // Some interesting reading about websockets https://stackoverflow.com/a/77060459
    const ws: Ref<Websocket | undefined> = ref();
    // Buffer messages in FIFO queue when disconnected (by client)... for example when ping interval is breached and reconnection is required
    const messageBuffer: Ref<RingQueue<string | Request>> = ref();
    const token: Ref<string | undefined> = ref();

    // Initialize WebSocket with buffering and exponential reconnection delay
    const init = () => {
        return (
            new WebsocketBuilder(getWsUrl())
                .withProtocols("v1.push.openbet.com")
                .withInstantReconnect(false)
                // .withBuffer(new RingQueue(99)) // buffer messages in FIFO queue when disconnected (by server)
                // .withBackoff(new ExponentialBackoff(2000, 5)) // retry every 2s, 4s, 8s, 16s, 32s, 64s
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .onOpen((_i, _ev) => {
                    if (extendedDebugging) console.log("ws opened!");
                })
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .onClose((_i, _ev) => {
                    if (extendedDebugging) console.log("ws closed!");
                })
                .build()
        );
    };
    const initMessageBuffer = () => {
        return new RingQueue<string | Request>(99);
    };
    messageBuffer.value = initMessageBuffer();

    const bufferMessage = (msg: string | Request) => {
        // Do not buffer ping messages
        if ((msg as Request)?.type === MessageType.Ping) return;
        if (extendedDebugging) console.log("message added to buffer", msg);
        messageBuffer.value.add(msg);
    };

    const resubscribeChannels = () => {
        const wsStore = useWsStore();
        if (!wsStore.subscribedChannels?.length) return;
        if (extendedDebugging) console.log("re-subscribed channels", wsStore.subscribedChannels);
        subscribeChannels(wsStore.subscribedChannels);
    };

    const sendBufferedMessages = () => {
        if (extendedDebugging) console.log("buffer", messageBuffer.value);
        for (let el = messageBuffer.value.read(); el !== undefined; el = messageBuffer.value.read()) {
            // Send buffered data
            if (typeof el === "string") {
                sendMessage(el, false);
            } else {
                sendRequest(el, false);
            }
        }
    };

    const getWsClient = (): Websocket | undefined => ws.value;

    const setToken = (tkn: string) => {
        token.value = tkn;
    };

    const openConnection = (additionalInit?: (ws: Websocket) => void, force?: boolean) => {
        if (extendedDebugging) console.log("ws open connection");
        if (!!ws.value && !!force) return;
        ws.value = init();
        if (additionalInit) additionalInit(ws.value);
    };

    const closeConnection = (force?: boolean) => {
        if (extendedDebugging) console.log("ws close connection");
        if (!ws.value && !!force) return;
        if (ws.value) ws.value.close();
        ws.value = undefined;
    };

    const resetConnection = (additionalInit?: (ws: Websocket) => void) => {
        if (extendedDebugging) console.log("ws reset connection");
        const force = () => ws.value !== undefined;
        closeConnection(force());
        openConnection(additionalInit, force());
    };

    const isValidRequest = <T extends Request>(req: T) => {
        let retVal = true;
        switch (req.type) {
            // Invalid subscribe/unsubscribe requests closes connection by server
            case MessageType.Subscribe:
            case MessageType.Unsubscribe:
                if (!(req as unknown as UnsubscribeRequest).channels.length) {
                    if (extendedDebugging) {
                        console.warn(
                            `invalid ${getEnumKeyByEnumValue(MessageType, req.type)} request: Channels empty!`,
                        );
                    }
                    retVal = false;
                }
                break;
            case MessageType.Ping:
                if (Number.isNaN((req as unknown as PingRequest).id)) {
                    if (extendedDebugging) {
                        console.warn(
                            `invalid ${getEnumKeyByEnumValue(MessageType, req.type)} request: Ping is not a number!`,
                        );
                    }
                    retVal = false;
                }
                break;
            default:
            // Do nothing here at this moment
        }
        return retVal;
    };

    // Prefer sendRequest(), might be removed later
    const sendMessage = (msg: string, buffer: boolean = true) => {
        if (!ws.value || ws.value.readyState !== 1) {
            if (buffer) bufferMessage(msg);
            return;
        }
        ws.value.send(msg);
    };

    const sendRequest = (req: Request, buffer: boolean = true) => {
        if (!ws.value || ws.value.readyState !== 1) {
            if (buffer) bufferMessage(req);
            return;
        }
        // Some basic validation to avoid connection disruption by server
        if (!isValidRequest(req)) return;
        ws.value.send(req.toString());
    };

    const sendInitialConnectionMessages = () => {
        // First of all we need to send connection request otherwise connection will be closed
        sendRequest(new ConnectionRequest(toValue(token) || ""), false);
        if (token.value) {
            // Cashout channel uses non-standard message format (we need to send it as string)
            sendMessage(`S0001${ChannelType.Cashout}${token.value.split(":")?.[1]}!!!!!!!!!!`, false);
        }
        // Re-subscribe to saved channels... applies only when connection was previously lost
        resubscribeChannels();
        // Sends buffered data if connection was disrupted and messages were sent meanwhile
        sendBufferedMessages();
    };

    const subscribeChannels = (channels: Channel[]) => {
        sendRequest(new SubscribeRequest(channels));
    };

    const unsubscribeChannels = (channels: Channel[]) => {
        sendRequest(new UnsubscribeRequest(channels));
    };

    const subscribeChannelsByType = (channelType: ChannelType, ids: number[]) => {
        const channels = ids.map((i) => ({ type: channelType, id: i }) as Channel);
        sendRequest(new SubscribeRequest(channels));
    };

    const unsubscribeChannelsByType = (channelType: ChannelType, ids: number[]) => {
        const channels = ids.map((i) => ({ type: channelType, id: i }) as Channel);
        sendRequest(new UnsubscribeRequest(channels));
    };

    return {
        getWsClient,
        setToken,
        openConnection,
        closeConnection,
        resetConnection,
        sendMessage,
        sendRequest,
        sendInitialConnectionMessages,
        subscribeChannels,
        unsubscribeChannels,
        subscribeChannelsByType,
        unsubscribeChannelsByType,
    };
}
